import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { describirError, errorDeCampo } from '@/api/cliente'
import { cambiarClave } from '@/api/auth'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { CampoConEtiqueta } from '@/componentes/Campos'
import { limpiarSesion } from './almacen'

/**
 * CAMBIAR LA CLAVE PROPIA DESDE EL PANEL (F1-13).
 *
 * Hasta el 05-oct-2026 solo existia en la app de terreno, y las cuentas con
 * segundo factor —municipio, administrador municipal, supervisor— no pueden
 * usar la app: se les entregaba una clave generada que no tenian donde
 * cambiar.
 *
 * Al cambiarla el servidor revoca TODAS las sesiones, incluida esta. Es lo que
 * se quiere —quien cambia la clave suele sospechar que alguien la vio— y por
 * eso, al terminar, se vuelve al login a entrar con la nueva.
 */
export default function CambioDeClave({
  email,
  abierto,
  alCerrar,
}: {
  email: string
  abierto: boolean
  alCerrar: () => void
}) {
  const navegar = useNavigate()
  const [actual, setActual] = useState('')
  const [nueva, setNueva] = useState('')
  const [repetida, setRepetida] = useState('')
  const [codigo, setCodigo] = useState('')

  const cambiar = useMutation({
    mutationFn: () => cambiarClave(email, actual, nueva, repetida, codigo.trim()),
    onSuccess: () => {
      limpiarSesion('clave_cambiada')
      navegar('/login', { replace: true })
    },
  })

  const error = cambiar.isError ? describirError(cambiar.error) : null
  const corta = nueva.length > 0 && nueva.length < 12
  const distintas = repetida.length > 0 && repetida !== nueva
  // El servidor pide el codigo solo a las cuentas con segundo factor: el campo
  // aparece cuando lo pide y se queda mientras haya algo escrito.
  const errorDeCodigo = errorDeCampo(error, 'codigo')
  const pideCodigo = errorDeCodigo !== undefined || codigo !== ''

  return (
    <Sheet
      open={abierto}
      onOpenChange={(abrir) => {
        if (!abrir) {
          alCerrar()
        }
      }}
    >
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Cambiar mi clave</SheetTitle>
          <SheetDescription>
            Al cambiarla se cierran todas tus sesiones abiertas, también esta. Después entras con la
            clave nueva.
          </SheetDescription>
        </SheetHeader>

        <form
          className="flex flex-col gap-4 px-4"
          onSubmit={(evento) => {
            evento.preventDefault()
            cambiar.mutate()
          }}
        >
          <CampoConEtiqueta
            etiqueta="Clave actual"
            htmlFor="clave-actual"
            error={errorDeCampo(error, 'clave_actual')}
          >
            <Input
              id="clave-actual"
              type="password"
              autoComplete="current-password"
              value={actual}
              onChange={(evento) => setActual(evento.currentTarget.value)}
            />
          </CampoConEtiqueta>

          <CampoConEtiqueta
            etiqueta="Clave nueva"
            htmlFor="clave-nueva"
            ayuda="Al menos 12 caracteres. Una frase que recuerdes sirve mejor que una palabra con símbolos."
            error={
              corta ? 'Tiene que tener al menos 12 caracteres.' : errorDeCampo(error, 'clave_nueva')
            }
          >
            <Input
              id="clave-nueva"
              type="password"
              autoComplete="new-password"
              value={nueva}
              onChange={(evento) => setNueva(evento.currentTarget.value)}
            />
          </CampoConEtiqueta>

          <CampoConEtiqueta
            etiqueta="Repite la clave nueva"
            htmlFor="clave-repetida"
            error={distintas ? 'No coincide con la clave nueva.' : undefined}
          >
            <Input
              id="clave-repetida"
              type="password"
              autoComplete="new-password"
              value={repetida}
              onChange={(evento) => setRepetida(evento.currentTarget.value)}
            />
          </CampoConEtiqueta>

          {pideCodigo && (
            <CampoConEtiqueta
              etiqueta="Código de tu aplicación de autenticación"
              htmlFor="clave-codigo"
              ayuda="El de seis dígitos que muestra ahora la app, o uno de tus códigos de recuperación."
              error={errorDeCodigo}
            >
              <Input
                id="clave-codigo"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={codigo}
                onChange={(evento) => setCodigo(evento.currentTarget.value)}
              />
            </CampoConEtiqueta>
          )}

          {error !== null && Object.keys(error.errores).length === 0 && (
            <Alert variant="destructive">
              <AlertDescription>{error.mensaje}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            disabled={cambiar.isPending || actual === '' || nueva.length < 12 || repetida !== nueva}
          >
            {cambiar.isPending ? 'Cambiando…' : 'Cambiar la clave'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  )
}
